import React, { useState, useEffect, useRef, useCallback } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Checkbox } from '@sparefoot/react-core';
import { H3 } from 'components/core/Typography';
import { Text } from 'components/core/Text';
import { ChevronDown } from 'components/svg';
import MenuItem from './MenuItem';
import withCollapsibleMenuHandlers from './withCollapsibleMenuHandlers';

const enhance = withCollapsibleMenuHandlers;

export const AccordionMenu = ({
	id,
	text,
	innerLinks,
	rightAlign,
	inline,
	isActive,
	handleClick,
	dropdownHeader,
}) => {
	const [openSections, setOpenSections] = useState({});
	const menuRef = useRef(null);
	const menuId = `menu-${id}`;

	const handleClickOutside = useCallback(
		(event) => {
			// Only handle click outside on desktop view
			if (window.innerWidth <= 1133) return;

			const { className } = event.target;
			const isInnerAccordionSafe =
				!!className &&
				typeof className === 'string' &&
				className.includes('inner-accordion-safe');

			const element = document.getElementById(menuId);
			if (
				element &&
				element.checked &&
				!isInnerAccordionSafe &&
				!menuRef.current?.contains(event.target)
			) {
				element.checked = false;
			}
		},
		[menuId]
	);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [handleClickOutside]);

	const toggleCheckbox = useCallback(() => {
		handleClick(text);
	}, [handleClick, text]);

	const toggleSection = useCallback((sectionTitle) => {
		setOpenSections((prevState) => ({
			...prevState,
			[sectionTitle]: !prevState[sectionTitle],
		}));
	}, []);

	const handleKeyDown = useCallback(
		(event, sectionTitle) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				toggleSection(sectionTitle);
			}
		},
		[toggleSection]
	);

	const hasSectionTitles = innerLinks && innerLinks.some((section) => section.sectionTitle);

	return (
		<div
			ref={menuRef}
			className={classnames('accordion-menu', {
				'menu-secondary': rightAlign,
			})}
		>
			{/* Uses "checkbox trick to make the header-accordion-button work for :checked css logic https://css-tricks.com/the-checkbox-hack/ */}
			<label
				htmlFor={menuId}
				className='header-accordion-button ph-link inner-accordion-safe'
			>
				<Text
					strong
					size='tiny'
					className='button-content'
				>
					{text}
				</Text>
				{innerLinks && <ChevronDown className='chevron-down' />}

				<Checkbox
					id={menuId}
					onChange={toggleCheckbox}
					className='visually-hidden'
					checked={isActive}
				/>
			</label>

			{innerLinks && (
				<div className='accordion-links'>
					{dropdownHeader && (
						<div className='dropdown-header hide-on-mobile'>
							<H3
								size='tall'
							>
								{dropdownHeader}
							</H3>
						</div>
					)}
					{hasSectionTitles ? (
						<div className='sections-container'>
							{innerLinks.map((section) => (
								<div
									key={section.sectionTitle}
									className='accordion-section'
								>
									<div className='section-accordion hide-on-desktop'>
										<div
											className={`section-accordion-button ${
												openSections[section.sectionTitle] ? 'active' : ''
											}`}
											onClick={() => toggleSection(section.sectionTitle)}
											onKeyDown={(event) =>
												handleKeyDown(event, section.sectionTitle)
											}
											role='button'
											tabIndex={0}
											aria-expanded={
												openSections[section.sectionTitle] || false
											}
										>
											<Text className='section-title-mobile'>
												{section.sectionTitle}
											</Text>
											<ChevronDown className='chevron-down' />
										</div>
										{openSections[section.sectionTitle] && (
											<div className='section-links'>
												{section.links.map((link) => (
													<MenuItem
														href={link.href}
														text={link.text}
														key={link.text}
														inline={inline}
													/>
												))}
											</div>
										)}
									</div>
									<Text className='show-on-desktop section-title-desktop'>
										{section.sectionTitle}
									</Text>
									<div className='desktop-section-links show-on-desktop'>
										{section.links.map((link) => (
											<MenuItem
												href={link.href}
												text={link.text}
												key={link.text}
												inline={inline}
											/>
										))}
									</div>
								</div>
							))}
						</div>
					) : (
						innerLinks.map((section) => (
							<MenuItem
								href={section.href}
								text={section.text}
								key={section.text}
								inline={inline}
							/>
						))
					)}
				</div>
			)}
		</div>
	);
};

AccordionMenu.propTypes = {
	inline: PropTypes.bool,
	innerLinks: PropTypes.arrayOf(PropTypes.object).isRequired,
	rightAlign: PropTypes.bool,
	text: PropTypes.string,
	id: PropTypes.string,
	isActive: PropTypes.bool,
	handleClick: PropTypes.func,
	dropdownHeader: PropTypes.string,
};

export default enhance(AccordionMenu);